import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  FormContainer,
  FormItemContainer,
  HeroLogo,
  IconButton,
  Label,
  Select,
  TextInput,
  VisibilityIcon,
  VisibilityOffIcon,
} from '../../components';
import { useFormValidation, useSubmitRegister } from './hooks';
import { Stack, styled } from '@mui/material';

const ActionGroup = styled(Stack)`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  > button {
    flex: 1;
  }
`;

const dialCodeOptions = [
  '+61',
  '+86',
  '+852',
  '+886',
  '+853',
  '+64',
  '+65',
  '+1',
  '+84',
];

export const UserRegisterForm = ({ onSuccess, onFail }) => {
  const [dialCode, setDialCode] = useState(dialCodeOptions[0]);
  const [visiblePassword, setVisiblePassword] = useState(false);
  const { submitRegister } = useSubmitRegister();
  const [submitFail, setSubmitFail] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    dialCode,
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  });

  const { validate, validationErrors: formError } = useFormValidation();

  const handleChange = useCallback(
    (value, name) => {
      setFormData((prev) => {
        const newFormData = {
          ...prev,
          [name]: value,
        };
        validate(newFormData, name);
        return newFormData;
      });
      setSubmitFail(false);
    },
    [validate]
  );

  const handleDialCodeChange = useCallback((value) => {
    setDialCode(value);
  }, []);

  const handleVisiblePasswordChange = useCallback(() => {
    setVisiblePassword((prev) => !prev);
  }, []);

  const onSubmit = useCallback(async () => {
    if (submitFail) return;
    const { isValid } = validate(formData);
    if (!isValid) return;
    await submitRegister(formData)
      .then((response) => onSuccess?.(response))
      .catch(() => {
        setSubmitFail(true);
        onFail?.();
      });
  }, [submitFail, validate, formData, submitRegister, onSuccess, onFail]);

  return (
    <FormContainer>
      <HeroLogo />
      <Stack gap="20px">
        {submitFail && <p>注册失败，请重试！</p>}
        <FormItemContainer>
          <Label isRequired>邮箱</Label>
          <TextInput
            placeholder="您的邮箱"
            name="email"
            value={formData.email}
            onChange={handleChange}
            error={formError?.email}
          />
        </FormItemContainer>
        <FormItemContainer>
          <Label isRequired>名字</Label>
          <TextInput
            name="firstName"
            placeholder="您的名字"
            value={formData.firstName}
            onChange={handleChange}
            error={formError?.firstName}
          />
        </FormItemContainer>
        <FormItemContainer>
          <Label isRequired>姓氏</Label>
          <TextInput
            name="lastName"
            placeholder="您的姓氏"
            value={formData.lastName}
            onChange={handleChange}
            error={formError?.lastName}
          />
        </FormItemContainer>
        <FormItemContainer>
          <Label isRequired>密码</Label>
          <TextInput
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="至少6位密码，区分大小写"
            type={visiblePassword ? 'text' : 'password'}
            endAdornment={
              <IconButton onClick={() => handleVisiblePasswordChange()}>
                {visiblePassword ? (
                  <VisibilityIcon size="20px" />
                ) : (
                  <VisibilityOffIcon size="20px" />
                )}
              </IconButton>
            }
            error={formError?.password}
          />
        </FormItemContainer>
        <FormItemContainer>
          <Label isRequired>确认密码</Label>
          <TextInput
            placeholder="再次输入密码"
            value={formData.confirmPassword}
            name="confirmPassword"
            type="password"
            onChange={handleChange}
            error={formError?.confirmPassword}
          />
        </FormItemContainer>
        <FormItemContainer count={3}>
          <Label isRequired>手机号</Label>
          <Select
            options={dialCodeOptions}
            initialValue={dialCode}
            onChange={handleDialCodeChange}
          />
          <TextInput
            placeholder="手机号"
            value={formData.phoneNumber}
            name="phoneNumber"
            onChange={handleChange}
            error={formError?.phoneNumber}
          />
        </FormItemContainer>
        <ActionGroup>
          <Button onClick={onSubmit}>注册</Button>
          <Link to="/user/login">已有账号?立即登录</Link>
        </ActionGroup>
      </Stack>
    </FormContainer>
  );
};
